import { Plus } from 'lucide-react';
import { Button, Typography } from '../../ui';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { MAESTRO_PILE_ID } from '../../../../lib/agent-pile';
import type { AgentSummary } from '../../../../lib/my-agents';
import type { useAgentName } from '../../../../lib/use-agent-name';
import { Genome } from '../genome/Genome';
import { YOUR_AGENTS_PAGE } from '../use-your-agents';
import { MobileAgentCards } from './MobileAgentCards';
import styles from './MobileTeamPanel.module.css';

// The narrow team band (Figma `8509:36899`): "Your Team · N" with the "+ Add Agent" ghost
// button under it and the primary "View N Skills" button opposite, then the genome and the
// roster carousel — all on one full-bleed surface that separates the team from the page.
//
// The count is the TEAM the Maestro leads, so the Maestro is not in it — matching the wide
// screen, where the same number sits over the same figure.

interface Props {
  agents: AgentSummary[];
  skillNames: string[];
  skillCount: number;
  maestro: ReturnType<typeof useAgentName>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onOpenSkills: () => void;
  onAddAgent: () => void;
}

export function MobileTeamPanel({
  agents,
  skillNames,
  skillCount,
  maestro,
  selectedId,
  onSelect,
  onOpenSkills,
  onAddAgent,
}: Props) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();

  return (
    <section className={styles.panel} aria-labelledby="agents-team-heading">
      <header className={styles.header}>
        <div className={styles.headings}>
          <Typography variant="h1" color="strong" component="h2" id="agents-team-heading" className={styles.title}>
            {t('agents.team')}
            <span className={styles.separator} aria-hidden />
            {agents.length}
          </Typography>
          <Button
            variant="text"
            size="small"
            startIcon={<Plus size={16} aria-hidden />}
            onClick={() => {
              logPageClickBi({ page_name: YOUR_AGENTS_PAGE, button_name: 'add_agent' });
              onAddAgent();
            }}
          >
            {t('agents.addAgent')}
          </Button>
        </div>
        <Button
          size="small"
          onClick={() => {
            logPageClickBi({ page_name: YOUR_AGENTS_PAGE, button_name: 'open_skills_panel' });
            onOpenSkills();
          }}
        >
          {t(skillCount === 1 ? 'agents.viewSkillsCountOne' : 'agents.viewSkillsCount', { count: skillCount })}
        </Button>
      </header>

      <div className={styles.figure}>
        <Genome
          agents={agents.map((agent) => ({ id: agent.id || agent.name, name: agent.name }))}
          skillNames={skillNames}
          maestroName={maestro.name}
          maestroId={MAESTRO_PILE_ID}
          selectedId={selectedId}
          onSelect={onSelect}
          nameReel
        />
      </div>

      <MobileAgentCards agents={agents} maestro={maestro} selectedId={selectedId} onSelect={onSelect} />
    </section>
  );
}
